/**
 * useAdminCategories Hook
 *
 * Fetches list of categories for admin using URQL query.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AdminCategoriesDocument = `
  query AdminCategories {
    categories(includeChildren: true) {
      id code name description color
      parent { id }
      sortOrder isActive eventCount
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface CategoryRaw {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  parent: { id: string } | null;
  sortOrder: number;
  isActive: boolean;
  eventCount: number;
}

export interface AdminCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  eventCount: number;
}

interface UseAdminCategoriesOptions {
  pause?: boolean;
}

interface UseAdminCategoriesResult {
  categories: AdminCategory[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch categories for admin management.
 */
export function useAdminCategories(
  options: UseAdminCategoriesOptions = {}
): UseAdminCategoriesResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ categories: CategoryRaw[] }>({
    query: AdminCategoriesDocument,
    pause,
  });

  const categories: AdminCategory[] = (result.data?.categories ?? []).map((c) => ({
    ...c,
    parentId: c.parent?.id ?? null,
  }));

  return {
    categories,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
